import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CreateMessageGenSessionRequest, MessageGenSession, MessageGenState } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import {
  type MessageGenerationCreateSession,
  MessageGenerationCreateSessionContext,
  MessageGenerationDispatchContext,
  MessageGenerationLoadSessionContext,
  MessageGenerationStateContext,
} from './Context';

interface Props {
  children: ReactNode;
}

export const MessageGenerationStateContainer = ({ children }: Props): ReactNode => {
  const apiClient = useChatApiClient();

  const [session, setSession] = useState<MessageGenSession | null>(null);

  const { mutate: createMutation } = useMutation({
    mutationFn: (data: CreateMessageGenSessionRequest) => apiClient.messageGeneration.createSession(data),
    onSuccess: (data) => {
      console.log('Created message generation session:', data);
      setSession(data);
    },
  });

  const loadSession = useCallback(
    (messageGenSessionId: number) => {
      apiClient.messageGeneration.getSession(messageGenSessionId).then((data) => {
        setSession(data);
      });
    },
    [apiClient]
  );

  const createSession: MessageGenerationCreateSession = useCallback(
    ({ brandIdentifier, sessionId, initialState }) => {
      console.log('Creating message generation session', sessionId, brandIdentifier, initialState);
      createMutation({ sessionId, brandIdentifier, initialState });
    },
    [createMutation]
  );

  const dispatch = useCallback((state: MessageGenState) => {
    console.log('Updating message generation session state:', state);
    setSession((prev) => (prev ? { ...prev, state } : prev));
  }, []);

  return (
    <MessageGenerationStateContext.Provider value={session}>
      <MessageGenerationDispatchContext.Provider value={dispatch}>
        <MessageGenerationCreateSessionContext.Provider value={createSession}>
          <MessageGenerationLoadSessionContext.Provider value={loadSession}>
            {children}
          </MessageGenerationLoadSessionContext.Provider>
        </MessageGenerationCreateSessionContext.Provider>
      </MessageGenerationDispatchContext.Provider>
    </MessageGenerationStateContext.Provider>
  );
};
